import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';

import { SiteNoteService } from '../../../core/api/site-note.service';
import { SuccessResponse } from '../../../core/api/site-verification-visits.service';
import * as fromApp from '../../../store';
import { DialogEditSiteNoteComponent } from '../../dialogs/dialog-edit-site-note/dialog-edit-site-note.component';
import { DialogYesNoComponent } from '../../dialogs/dialog-yes-no/dialog-yes-no.component';
import { Site, SiteNote } from '../../models';
import { ToastLevel } from '../../models/toast-message.module';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'fc-site-detail-notes',
  templateUrl: './site-detail-notes.component.html',
  styleUrls: ['./site-detail-notes.component.scss'],
})
export class SiteDetailNotesComponent implements OnInit {
  @ViewChild('matExpansionPanel', { static: false })
  matExpansionPanel: MatExpansionPanel;

  destroyRef = inject(DestroyRef);
  displayedColumns: string[] = ['id', 'category', 'note', 'actions'];

  @Input()
  site: Site;

  @Input()
  currentUser = null;

  hasNeedsReview = false;

  siteNotes: SiteNote[] = [];
  dataSource: MatTableDataSource<any>;
  categories: string[] = [];

  noteForm: FormGroup;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly siteNoteService: SiteNoteService,
    readonly toastService: ToastService,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.siteNoteService.getSiteNoteCategories().subscribe((categories: string[]) => {
      this.categories = categories;
    }); // Preload categories
    this.resetForm();
    this.refreshNotes();
  }

  refreshNotes() {
    this.siteNoteService.getSiteNotes(this.site.id).subscribe(siteNotes => {
      this.hasNeedsReview = siteNotes.some(
        (note: SiteNote) => note.needsReview && (this.currentUser.isAdmin || !note.hideFromLimitedAdmins)
      );
      this.siteNotes = siteNotes;
      this.dataSource = new MatTableDataSource(siteNotes);
      this.dataSource.filterPredicate = (data: SiteNote, filter: string) => {
        // If no filter, show all
        if (!filter) {
          return true;
        }
        // Compare the lower-case status to the filter (assumed to be lower-case too)
        return data.category === filter;
      };
      this.dataSource.sort = this.sort;
    });
  }

  resetForm() {
    this.noteForm = new FormGroup({
      note: new FormControl(''), // For the note input field
      hideFromLimitedAdmins: new FormControl(true), // For the checkbox, defaulting to false
    });
  }

  updateVisibility(site: SiteNote) {
    let updateSiteNote = new SiteNote(site);
    updateSiteNote.hideFromLimitedAdmins = !site.hideFromLimitedAdmins;
    this.siteNoteService.updateSiteNote(updateSiteNote).subscribe(any => {
      this.toastService.sendMessage('Visibility updated successfully', ToastLevel.SUCCESS);
      this.refreshNotes();
    });
  }

  addUpdateNote(note: SiteNote = null): void {
    this.dialog
      .open(DialogEditSiteNoteComponent, {
        panelClass: 'fc-edit-site-note-dialog',
        data: {
          siteNote: note,
          siteId: this.site.id,
          categories: this.categories,
          currentUser: this.currentUser,
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result: SiteNote) => {
        if (result) {
          this.toastService.sendMessage(
            'Site note ' + (note ? 'updated' : 'created') + ' successfully',
            ToastLevel.SUCCESS
          );
          this.refreshNotes();
        }
      });
  }

  deleteNote(id: number): void {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Confirm Delete!',
          line1: 'Are you sure you want to delete this site note?',
          buttonTextYes: 'Yes, Delete',
          buttonTextNo: 'Cancel',
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe(result => {
        if (result !== null && result === true) {
          this.siteNoteService.deleteSiteNote(id).subscribe((response: SuccessResponse) => {
            if (response && response.success) {
              this.refreshNotes();
              this.toastService.sendMessage('Site note deleted successfully', ToastLevel.SUCCESS);
            }
          });
        }
      });
  }

  applyFilter(filterValue: any) {
    this.dataSource.filter = filterValue;
  }
}
